import React, { memo, PropsWithChildren, useEffect, useMemo, useState } from "react";

import { UnitTypes } from "@/constants/bodyMetrics";
import { CountryCodes } from "@/constants/currency";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { useGeolocation } from "@/utils/GeolocationProvider";
import { email as paramEmail } from "@/utils/settings";

import { localStorageManager, LocalStorageManagerKeys } from "./localStorageManager";

export const TARGET_WEIGHT_STRING_KEY = "frontTargetWeightString";
export const CURRENT_WEIGHT_STRING_KEY = "frontCurrentWeightString";

export type AnswerValueType =
    | boolean
    | string
    | number
    | string[]
    | number[]
    | null
    | Date
    | object[];
export type Answers = Record<string, AnswerValueType>;

export interface AnswersProviderProps {
    answers: Answers;
    setAnswers: React.Dispatch<React.SetStateAction<Answers>>;
    name: string;
    setName: React.Dispatch<React.SetStateAction<string>>;
    email: string;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    isSkipDateChart: boolean;
}

const defaultState: AnswersProviderProps = {
    answers: {},
    setAnswers: () => null,
    name: "",
    setName: () => null,
    email: "",
    setEmail: () => null,
    isSkipDateChart: false,
};

export const AnswersContext = React.createContext<AnswersProviderProps>(defaultState);

const AnswersProvider = ({ children }: PropsWithChildren) => {
    const [answers, setAnswers] = useState(defaultState.answers);
    const savedName = localStorageManager.getStoredItem(LocalStorageManagerKeys.NAME) as
        | string
        | undefined;
    const [name, setName] = useState(savedName ?? defaultState.name);
    const savedEmail = localStorageManager.getStoredItem(LocalStorageManagerKeys.EMAIL) as
        | string
        | undefined;
    const [email, setEmail] = useState(savedEmail ?? defaultState.email);

    const isSkipDateChart = useMemo(
        () =>
            answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] === "none" ||
            answers?.[QUESTIONS_IDS.EVENT_DATE] === "skip",
        [answers]
    );

    const { country } = useGeolocation();

    useEffect(() => {
        email && localStorageManager.storeItem(LocalStorageManagerKeys.EMAIL, email);
    }, [email]);

    useEffect(() => {
        name && localStorageManager.storeItem(LocalStorageManagerKeys.NAME, name);
    }, [name]);

    useEffect(() => {
        if (paramEmail) {
            setEmail(paramEmail);
        }
    }, [paramEmail]);

    useEffect(() => {
        setAnswers((prev) => ({
            ...prev,
            units: country === CountryCodes.US ? UnitTypes.Imperial : UnitTypes.Metric,
        }));
    }, [country]);

    return (
        <AnswersContext.Provider
            value={{
                answers,
                setAnswers,
                name,
                setName,
                email,
                setEmail,
                isSkipDateChart,
            }}
        >
            {children}
        </AnswersContext.Provider>
    );
};

export default memo(AnswersProvider);
